import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Badge,
  Text,
  Box,
  Flex,
  Icon,
  Divider,
  useColorModeValue,
  SimpleGrid,
  Card,
  CardBody,
  VStack,
  HStack
} from '@chakra-ui/react';
import { FiClock, FiWifi, FiWifiOff, FiActivity } from 'react-icons/fi';

const ClientOfflineModal = ({ 
  isOpen, 
  onClose, 
  clientHistoryName, 
  clientHealthHistory,
  formatDateTime,
  calculateDuration 
}) => {
  
  // Enhanced dark theme colors
  const bgGradient = useColorModeValue(
    'linear(to-br, #f8fafc, #e2e8f0)',
    'linear(to-br, #0f172a, #1e293b)'
  );
  
  const headerBg = useColorModeValue(
    'linear(to-r, #ffffff, #f7fafc)',
    'linear(to-r, #1e293b, #334155)'
  );
  
  const borderColor = useColorModeValue('#e2e8f0', '#334155');
  const cardBg = useColorModeValue('#ffffff', '#1e293b');
  const textColor = useColorModeValue('#2d3748', '#f1f5f9');
  const headerTextColor = useColorModeValue('#718096', '#94a3b8');
  const hoverBg = useColorModeValue('blue.50', 'whiteAlpha.50');
  
  // Enhanced card shadows for dark theme
  const cardShadow = useColorModeValue(
    '0 4px 20px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.1)',
    '0 4px 20px rgba(0, 0, 0, 0.5), 0 1px 3px rgba(0, 0, 0, 0.4)'
  );
  
  const cardHoverShadow = useColorModeValue(
    '0 8px 30px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.08)',
    '0 8px 30px rgba(0, 0, 0, 0.7), 0 4px 8px rgba(0, 0, 0, 0.5)'
  );
  
  // Empty state colors
  const emptyStateBg = useColorModeValue('#f7fafc', '#334155');
  const emptyStateIconColor = useColorModeValue('#a0aec0', '#64748b');
  const emptyStateTextColor = useColorModeValue('#718096', '#94a3b8');
  
  // Status colors - enhanced for dark theme
  const offlineBg = useColorModeValue(
    'linear(to-br, #fed7d7, #feb2b2)',
    'linear(to-br, #7f1d1d, #991b1b)'
  );
  const offlineBorder = useColorModeValue('#fc8181', '#dc2626');
  const offlineText = useColorModeValue('#c53030', '#fca5a5');
  
  const onlineBg = useColorModeValue(
    'linear(to-br, #c6f6d5, #9ae6b4)',
    'linear(to-br, #14532d, #166534)'
  );
  const onlineBorder = useColorModeValue('#68d391', '#22c55e');
  const onlineText = useColorModeValue('#2f855a', '#86efac');
  
  // Duration colors - better contrast for dark theme
  const durationBg = useColorModeValue(
    'linear(to-br, #fef5e7, #fed7aa)',
    'linear(to-br, #78350f, #92400e)'
  );
  const durationBorder = useColorModeValue('#f6ad55', '#f59e0b');
  const durationText = useColorModeValue('#c05621', '#fbbf24');
  
  // Badge colors
  const badgeBg = useColorModeValue('#ffffff', '#334155');
  const badgeShadow = useColorModeValue(
    '0 2px 8px rgba(0, 0, 0, 0.1)',
    '0 2px 8px rgba(0, 0, 0, 0.4)'
  );
  
  // Accent colors for monitoring theme
  const accentPrimary = useColorModeValue('#3182ce', '#60a5fa');
  const accentSecondary = useColorModeValue('#805ad5', '#a78bfa');
  
  // Additional missing variables
  const blueBg = useColorModeValue('#ebf8ff', '#1e3a8a');
  const grayBg = useColorModeValue('#f7fafc', '#374151');
  const grayBg2 = useColorModeValue('#edf2f7', '#1f2937');
  const grayHoverBg = useColorModeValue('#e2e8f0', '#374151');
  
  // Section background colors - improved dark theme
  const offlineSectionBg = useColorModeValue('#fef2f2', '#7f1d1d');
  const offlineSectionText = useColorModeValue('#dc2626', '#fca5a5');
  const onlineSectionBg = useColorModeValue('#f0fdf4', '#14532d');
  const onlineSectionText = useColorModeValue('#16a34a', '#86efac');
  const durationSectionBg = useColorModeValue('#fffbeb', '#78350f');
  const durationSectionBorder = useColorModeValue('#fbbf24', '#f59e0b');
  const durationSectionText = useColorModeValue('#92400e', '#fbbf24');
  
  // Heartbeat graph colors
  const heartbeatStroke = useColorModeValue('#e53e3e', '#f87171');
  const heartbeatDurationBar = useColorModeValue('#f59e0b', '#fbbf24');
  
  // Professional graph colors
  const graphBlueBg = useColorModeValue('blue.100', 'blue.800');
  
  const renderProfessionalGraph = () => {
    if (clientHealthHistory.length === 0) {
      return (
        <Box textAlign="center" py={16}>
          <Box
            p={8}
            borderRadius="xl"
            bg={emptyStateBg}
            display="inline-block"
            mb={6}
            border="2px dashed"
            borderColor={borderColor}
          >
            <Icon as={FiActivity} size="48px" color={emptyStateIconColor} mb={4} />
            <Text fontSize="xl" fontWeight="600" color={textColor} mb={2}>
              No Connection Events
            </Text>
            <Text fontSize="md" color={emptyStateTextColor}>
              Client maintains perfect connectivity
            </Text>
          </Box>
        </Box>
      );
    }

    // Get top 10 recent events and create monitoring data
    const recentEvents = clientHealthHistory
      .sort((a, b) => new Date(b.lastOfflineTime) - new Date(a.lastOfflineTime))
      .slice(0, 10);
      
    const monitoringData = recentEvents.map((event, index) => {
      const offlineTime = new Date(event.lastOfflineTime);
      const onlineTime = new Date(event.lastOnlineTime);
      const duration = (onlineTime - offlineTime) / (1000 * 60); // minutes
      
      // Calculate responsive spacing based on number of events
      const totalEvents = recentEvents.length;
      const containerWidth = 1000; // Base container width
      const margins = 100; // Left and right margins
      const availableWidth = containerWidth - margins;
      const eventSpacing = availableWidth / (totalEvents + 1);
      const eventWidth = Math.min(Math.max(eventSpacing * 0.6, 30), 60); // Responsive bar width
      
      return {
        id: event.historyId,
        offlineTime,
        onlineTime,
        duration,
        status: event.status,
        x: margins/2 + eventSpacing * (index + 1), // Evenly distributed
        barWidth: eventWidth,
        severity: duration > 60 ? 'critical' : duration > 30 ? 'warning' : 'minor'
      };
    });

    const maxWidth = 1000; // Fixed responsive width
    const maxDuration = Math.max(...monitoringData.map(d => d.duration));
    
    // Dynamic Y-axis scaling
    const getYAxisMax = (maxDur) => {
      if (maxDur <= 30) return 30;
      if (maxDur <= 60) return 60;
      if (maxDur <= 120) return 120;
      if (maxDur <= 240) return 240;
      if (maxDur <= 480) return 480;
      if (maxDur <= 720) return 720;
      if (maxDur <= 1440) return 1440; // 24 hours
      return Math.ceil(maxDur / 1440) * 1440; // Round up to next day
    };
    
    const yAxisMax = getYAxisMax(maxDuration);
    
    // Create appropriate step intervals - Fixed for better spacing
    const getStepIntervals = (max) => {
      if (max <= 30) return [0, 10, 20, 30];
      if (max <= 60) return [0, 20, 40, 60];
      if (max <= 90) return [0, 30, 60, 90];
      if (max <= 120) return [0, 40, 80, 120];
      if (max <= 180) return [0, 60, 120, 180];
      if (max <= 240) return [0, 80, 160, 240];
      if (max <= 360) return [0, 120, 240, 360];
      if (max <= 480) return [0, 160, 320, 480];
      if (max <= 720) return [0, 240, 480, 720];
      if (max <= 1440) return [0, 480, 960, 1440];
      return [0, max/3, max*2/3, max];
    };
    
    const stepIntervals = getStepIntervals(yAxisMax);
    
    // Format labels to show proper units
    const formatYAxisLabel = (val) => {
      if (val === 0) return '0';
      if (val >= 1440) return `${Math.round(val / 1440 * 10) / 10}d`;
      if (val >= 60) {
        const hours = val / 60;
        if (Number.isInteger(hours)) {
          return `${hours}h`;
        } else {
          return `${Math.round(hours * 10) / 10}h`;
        }
      }
      return `${val}m`;
    };

    return (
      <Box p={6}>
        {/* Header Section */}
        <VStack spacing={4} mb={8}>
          <HStack spacing={3}>
            <Box
              p={3}
              borderRadius="xl"
              bg={graphBlueBg}
            >
              <Icon as={FiActivity} size="24px" color="blue.600" />
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontSize="2xl" fontWeight="800" color={textColor}>
                Client Health Monitoring
              </Text>
              <Text fontSize="md" color={headerTextColor}>
                Recent offline events - Real-time analysis
              </Text>
            </VStack>
          </HStack>
          
          {/* Metrics Dashboard */}
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6} w="100%">
            <Box textAlign="center" p={4} bg={cardBg} borderRadius="lg" border="1px solid" borderColor={borderColor}>
              <Text fontSize="2xl" fontWeight="800" color="#3182ce">
                {recentEvents.length}
              </Text>
              <Text fontSize="sm" color={headerTextColor} fontWeight="600">
                Recent Events
              </Text>
            </Box>
            <Box textAlign="center" p={4} bg={cardBg} borderRadius="lg" border="1px solid" borderColor={borderColor}>
              <Text fontSize="2xl" fontWeight="800" color="#e53e3e">
                {monitoringData.filter(d => d.severity === 'critical').length}
              </Text>
              <Text fontSize="sm" color={headerTextColor} fontWeight="600">
                Critical Issues
              </Text>
            </Box>
            <Box textAlign="center" p={4} bg={cardBg} borderRadius="lg" border="1px solid" borderColor={borderColor}>
              <Text fontSize="2xl" fontWeight="800" color="#38a169">
                {monitoringData.filter(d => d.status === 1).length}
              </Text>
              <Text fontSize="sm" color={headerTextColor} fontWeight="600">
                Resolved
              </Text>
            </Box>
            <Box textAlign="center" p={4} bg={cardBg} borderRadius="lg" border="1px solid" borderColor={borderColor}>
              <Text fontSize="2xl" fontWeight="800" color="#f59e0b">
                {Math.round(monitoringData.reduce((sum, d) => sum + d.duration, 0))}m
              </Text>
              <Text fontSize="sm" color={headerTextColor} fontWeight="600">
                Total Downtime
              </Text>
            </Box>
          </SimpleGrid>
        </VStack>

        {/* Main Graph - Bigger Size */}
        <Box 
          bg={cardBg} 
          borderRadius="xl" 
          border="1px solid" 
          borderColor={borderColor}
          p={8}
          mb={6}
          boxShadow={cardShadow}
        >
          <Flex justify="space-between" align="center" mb={6}>
            <VStack align="start" spacing={1}>
              <Text fontSize="xl" fontWeight="700" color={textColor}>
                Connection Timeline & Downtime Analysis
              </Text>
              <Text fontSize="md" color={headerTextColor}>
                Recent events with enhanced date/time visualization
              </Text>
            </VStack>
            
            {/* Legend */}
            <HStack spacing={4}>
              <HStack spacing={2}>
                <Box w={3} h={3} borderRadius="full" bg="#22c55e" />
                <Text fontSize="sm" color={headerTextColor}>Minor (&lt;30m)</Text>
              </HStack>
              <HStack spacing={2}>
                <Box w={3} h={3} borderRadius="full" bg="#f59e0b" />
                <Text fontSize="sm" color={headerTextColor}>Warning (30-60m)</Text>
              </HStack>
              <HStack spacing={2}>
                <Box w={3} h={3} borderRadius="full" bg="#ef4444" />
                <Text fontSize="sm" color={headerTextColor}>Critical (&gt;60m)</Text>
              </HStack>
            </HStack>
          </Flex>
          
          <Box position="relative" height="450px" w="100%">
            <svg
              width="100%"
              height="450"
              viewBox={`0 0 ${maxWidth} 450`}
              preserveAspectRatio="xMidYMid meet"
              style={{ maxWidth: '100%', height: 'auto' }}
            >
              {/* Grid */}
              <defs>
                <pattern id="monitoringGrid" width="40" height="25" patternUnits="userSpaceOnUse">
                  <path d="M 40 0 L 0 0 0 25" fill="none" stroke={borderColor} strokeWidth="0.5" opacity="0.2"/>
                </pattern>
                <linearGradient id="criticalGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#ef4444" stopOpacity="0.8"/>
                  <stop offset="100%" stopColor="#ef4444" stopOpacity="0.2"/>
                </linearGradient>
                <linearGradient id="warningGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.8"/>
                  <stop offset="100%" stopColor="#f59e0b" stopOpacity="0.2"/>
                </linearGradient>
                <linearGradient id="minorGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#22c55e" stopOpacity="0.8"/>
                  <stop offset="100%" stopColor="#22c55e" stopOpacity="0.2"/>
                </linearGradient>
              </defs>
              
              <rect width="100%" height="100%" fill="url(#monitoringGrid)" />
              
              {/* Baseline and reference lines */}
              <line x1="0" y1="350" x2={maxWidth} y2="350" stroke={headerTextColor} strokeWidth="2" strokeDasharray="3,3" opacity="0.6"/>
              {stepIntervals.slice(1).map((value, i) => {
                const y = 350 - (value / yAxisMax) * 200;
                return (
                  <line 
                    key={i}
                    x1="0" 
                    y1={y} 
                    x2={maxWidth} 
                    y2={y} 
                    stroke={headerTextColor} 
                    strokeWidth="1" 
                    strokeDasharray="2,4" 
                    opacity="0.4"
                  />
                );
              })}
              
              {/* Y-axis labels */}
              {stepIntervals.map((value, i) => {
                const y = 350 - (value / yAxisMax) * 200 + 5;
                
                return (
                  <text key={`label-${value}`} x="15" y={y} fontSize="13" fill={headerTextColor} fontWeight="700">
                    {formatYAxisLabel(value)}
                  </text>
                );
              })}
              
              {/* Data visualization */}
              {monitoringData.map((point, index) => {
                const barHeight = (point.duration / yAxisMax) * 200; // Scale to Y-axis max
                const barY = 350 - barHeight;
                const color = point.severity === 'critical' ? '#ef4444' : 
                             point.severity === 'warning' ? '#f59e0b' : '#22c55e';
                const gradient = point.severity === 'critical' ? 'url(#criticalGradient)' : 
                               point.severity === 'warning' ? 'url(#warningGradient)' : 'url(#minorGradient)';
                
                return (
                  <g key={point.id}>
                    {/* Duration bar */}
                    <rect
                      x={point.x - point.barWidth/2}
                      y={barY}
                      width={point.barWidth}
                      height={barHeight}
                      fill={gradient}
                      stroke={color}
                      strokeWidth="2"
                      rx="6"
                      style={{
                        filter: `drop-shadow(0 4px 12px ${color}40)`
                      }}
                    />
                    
                    {/* Event marker */}
                    <circle
                      cx={point.x}
                      cy={barY - 20}
                      r={Math.min(point.barWidth * 0.3, 12)}
                      fill={point.status === 1 ? '#38a169' : '#ef4444'}
                      stroke="white"
                      strokeWidth="3"
                      style={{
                        filter: `drop-shadow(0 3px 8px ${point.status === 1 ? '#38a169' : '#ef4444'}60)`
                      }}
                    />
                    
                    {/* Event ID */}
                    <text
                      x={point.x}
                      y={barY - 15}
                      textAnchor="middle"
                      fontSize={Math.min(point.barWidth * 0.25, 12)}
                      fontWeight="700"
                      fill="white"
                    >
                      {point.id}
                    </text>
                    
                    {/* Duration label - Enhanced for large values */}
                    <text
                      x={point.x}
                      y={barY + barHeight + 20}
                      textAnchor="middle"
                      fontSize={Math.min(point.barWidth * 0.22, 12)}
                      fontWeight="700"
                      fill={color}
                    >
                      {point.duration >= 1440 ? 
                        `${Math.round(point.duration / 1440 * 10) / 10}d` : 
                        point.duration >= 60 ? 
                        `${Math.round(point.duration / 60 * 10) / 10}h` : 
                        `${Math.round(point.duration)}m`
                      }
                    </text>
                    
                    {/* Enhanced Date Display */}
                    <text
                      x={point.x}
                      y="380"
                      textAnchor="middle"
                      fontSize={Math.min(point.barWidth * 0.2, 11)}
                      fontWeight="600"
                      fill={textColor}
                    >
                      {point.offlineTime.toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: '2-digit' 
                      })}
                    </text>
                    
                    {/* Enhanced Time Display */}
                    <text
                      x={point.x}
                      y="395"
                      textAnchor="middle"
                      fontSize={Math.min(point.barWidth * 0.18, 10)}
                      fontWeight="500"
                      fill={headerTextColor}
                    >
                      {point.offlineTime.toLocaleTimeString('en-US', { 
                        hour: '2-digit', 
                        minute: '2-digit',
                        hour12: true 
                      })}
                    </text>
                    
                    {/* Year Display */}
                    <text
                      x={point.x}
                      y="410"
                      textAnchor="middle"
                      fontSize={Math.min(point.barWidth * 0.16, 9)}
                      fontWeight="400"
                      fill={headerTextColor}
                      opacity="0.7"
                    >
                      {point.offlineTime.getFullYear()}
                    </text>
                  </g>
                );
              })}
              
              {/* Chart title */}
              <text x="50" y="40" fontSize="16" fontWeight="800" fill={textColor}>
                Top 10 Recent Downtime Events
              </text>
              <text x="50" y="60" fontSize="13" fill={headerTextColor}>
                Each bar represents offline duration with enhanced date/time labels
              </text>
            </svg>
          </Box>
        </Box>

        {/* Status Summary - Top 5 Recent */}
        <Box 
          bg={cardBg} 
          borderRadius="xl" 
          border="1px solid" 
          borderColor={borderColor}
          p={6}
          boxShadow={cardShadow}
        >
          <Text fontSize="lg" fontWeight="700" color={textColor} mb={4}>
            Top 5 Recent Events Summary
          </Text>
          
          <VStack spacing={3} align="stretch">
            {monitoringData.slice(0, 5).map((event) => (
              <Flex key={event.id} justify="space-between" align="center" p={4} bg={emptyStateBg} borderRadius="lg">
                <HStack spacing={4}>
                  <Box
                    w={4}
                    h={4}
                    borderRadius="full"
                    bg={event.severity === 'critical' ? '#ef4444' : 
                        event.severity === 'warning' ? '#f59e0b' : '#22c55e'}
                    boxShadow={`0 0 8px ${event.severity === 'critical' ? '#ef4444' : 
                               event.severity === 'warning' ? '#f59e0b' : '#22c55e'}40`}
                  />
                  <VStack align="start" spacing={0}>
                    <Text fontSize="md" fontWeight="700" color={textColor}>
                      Event #{event.id}
                    </Text>
                    <Text fontSize="sm" color={headerTextColor} fontWeight="500">
                      {event.offlineTime.toLocaleDateString('en-US', { 
                        weekday: 'short',
                        month: 'short', 
                        day: '2-digit',
                        year: 'numeric'
                      })} at {event.offlineTime.toLocaleTimeString('en-US', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </Text>
                  </VStack>
                </HStack>
                
                <HStack spacing={6}>
                  <VStack spacing={0} align="end">
                    <Text fontSize="lg" fontWeight="700" color={textColor}>
                      {Math.round(event.duration)}m
                    </Text>
                    <Text fontSize="xs" color={headerTextColor}>
                      Duration
                    </Text>
                  </VStack>
                  <Badge
                    colorScheme={event.status === 1 ? 'green' : 'red'}
                    variant="subtle"
                    borderRadius="full"
                    px={4}
                    py={2}
                    fontSize="sm"
                    fontWeight="600"
                  >
                    {event.status === 1 ? 'Resolved' : 'Active'}
                  </Badge>
                </HStack>
              </Flex>
            ))}
          </VStack>
        </Box>
      </Box>
    );
  }

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      size="6xl" 
      scrollBehavior="inside"
      motionPreset="slideInBottom"
    >
      <ModalOverlay 
        bg="blackAlpha.600" 
        backdropFilter="blur(8px)"
      />
      <ModalContent
        bg={cardBg}
        borderRadius="2xl"
        boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.25)"
        border="1px solid"
        borderColor={borderColor}
        mx={4}
        my={8}
      >
        <ModalHeader 
          bg={headerBg}
          borderTopRadius="2xl"
          borderBottom="1px solid"
          borderColor={borderColor}
          py={6}
          px={8}
        >
          <Flex align="center" justify="space-between">
            <Box>
              <Flex align="center" gap={3}>
                <Box 
                  p={2} 
                  bg={blueBg} 
                  borderRadius="lg"
                >
                  <Icon as={FiActivity} size="20px" color="blue.600" />
                </Box>
                <Box>
                  <Text fontSize="xl" fontWeight="700" color={textColor}>
                    Client Offline History
                  </Text>
                  <Text fontSize="sm" color="gray.500" fontWeight="500">
                    {clientHistoryName}
                  </Text>
                </Box>
              </Flex>
            </Box>
            <Box 
              bg={grayBg} 
              px={3} 
              py={1} 
              borderRadius="full"
            >
              <Text fontSize="xs" fontWeight="600" color="gray.600">
                {clientHealthHistory.length} Records
              </Text>
            </Box>
          </Flex>
        </ModalHeader>
        
        <ModalCloseButton 
          size="lg"
          top={4}
          right={4}
          bg="white"
          color="gray.600"
          _hover={{ 
            bg: "red.50", 
            color: "red.500",
            transform: "scale(1.1)"
          }}
          _active={{
            bg: "red.100",
            transform: "scale(0.95)"
          }}
          borderRadius="full"
          border="2px solid"
          borderColor="gray.200"
          boxShadow="0 4px 12px rgba(0, 0, 0, 0.1)"
          transition="all 0.2s ease"
          onClick={onClose}
          fontSize="16px"
          fontWeight="bold"
        />
        
        <ModalBody p={0}>
          <Box bg={bgGradient} minH="700px">
            {renderProfessionalGraph()}
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ClientOfflineModal;





 